import {useStoreActions} from 'store';
import {useEffect} from 'react';

// Global event emitter for date-dependent data refresh
class DateRefreshEventEmitter {
    private listeners: (() => void)[] = [];
    
    subscribe(callback: () => void): () => void {
        this.listeners.push(callback);
        return () => {
            const index = this.listeners.indexOf(callback);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }
    
    emit(): void {
        this.listeners.forEach(callback => callback());
    }
}

export const dateRefreshEmitter = new DateRefreshEventEmitter();

/**
 * Hook to refresh all data that depends on session date ranges
 * This should be called whenever the date range is updated
 */
export const useDateDependentDataRefresh = () => {
    // Get all the setReload actions for stores that depend on date data and have setReload method
    // Only stores created with simpleCrudStoreFactory() or custom stores with setReload have this method
    const setStoryboardsReload = useStoreActions((actions) => actions.storyboards.setReload);
    
    // SimpleCrudStoreFactory stores that depend on date data
    const setCustomersReload = useStoreActions((actions) => actions.customers.setReload);
    const setAgentsReload = useStoreActions((actions) => actions.agents.setReload);
    const setTeamsReload = useStoreActions((actions) => actions.teams.setReload);
    const setUsersReload = useStoreActions((actions) => actions.users.setReload);
    const setChannelsReload = useStoreActions((actions) => actions.channels.setReload);
    const setBackgroundTasksReload = useStoreActions((actions) => actions.backgroundTasks.setReload);
    const setEvaluationsReload = useStoreActions((actions) => actions.evaluations.setReload);
    const setPublicStoriesReload = useStoreActions((actions) => actions.publicStories.setReload);
    const setConversationStoriesReload = useStoreActions((actions) => actions.conversationStories.setReload);
    const setPersonsStoriesReload = useStoreActions((actions) => actions.personsStories.setReload);
    
    // Additional SimpleCrudStoreFactory stores that may depend on date data
    const setApiTokensReload = useStoreActions((actions) => actions.apiTokens.setReload);
    const setDictionariesReload = useStoreActions((actions) => actions.dictionaries.setReload);
    
    // Custom stores with setReload methods that depend on date data
    const setUserDataReload = useStoreActions((actions) => actions.userData.setReload);
    const setEventsReload = useStoreActions((actions) => actions.events.setReload);

    const refreshAllDateDependentData = () => {
        
        // Trigger reload for all date-dependent stores that have setReload method
        setStoryboardsReload(true);
        setCustomersReload(true);
        setAgentsReload(true);
        setTeamsReload(true);
        setUsersReload(true);
        setChannelsReload(true);
        setBackgroundTasksReload(true);
        setEvaluationsReload(true);
        setPublicStoriesReload(true);
        setConversationStoriesReload(true);
        setPersonsStoriesReload(true);
        
        // Additional stores
        setApiTokensReload(true);
        setDictionariesReload(true);
        setUserDataReload(true);
        setEventsReload(true);
        
        // Also emit global event for components that don't use standard stores
        dateRefreshEmitter.emit();
        
    };

    return refreshAllDateDependentData;
};

/**
 * Hook for components to listen to date range changes
 * Use this in components that need to refresh data when dates change
 * but don't use the standard store pattern
 */
export const useDateChangeListener = (callback: () => void) => {
    useEffect(() => {
        const unsubscribe = dateRefreshEmitter.subscribe(callback);
        return unsubscribe;
    }, [callback]);
};
